/* eslint-disable react-hooks/exhaustive-deps */

import React, {useState, useEffect, useCallback, useContext} from 'react';
import Group from './Group';
import {
  addNewGroup,
  convertToMinutes,
  updateConditionType,
  updateRule,
} from '../hooks/ruleUtils';
import {ChevronDown, ChevronUp} from 'lucide-react';
import {
  detectTooltip,
  plusBlue,
  toolTipTopDark,
  trashDarkRedTwo,
} from 'assets/images/images';
import {customStyles, DropdownIndicator} from 'components';
import Select from 'react-select';
import {RULE_TYPE} from '../../EditProcess/transactions';
import {CreditCheckContext} from '../../../../../helpers/context/CreditCheckContext';
import {ConfirmModal} from '../modals/ConfirmModals';
import {useFlowData} from '../../EditProcess/hooks/useFlowData';
import {TimePeriod} from '../components/TimePeriod';
import {EasyDetectContext} from '../../../../../helpers/context/EasyDetectContext';

function Rule({rule, rules, setRules, onDelete}) {
  const [isActionOpen, setIsActionOpen] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [isRuleOpen, setIsRuleOpen] = useState(rule.isRuleOpen);
  const {get} = useContext(CreditCheckContext);
  const {isAggregateFunction} = useContext(EasyDetectContext);
  const FLowData = useFlowData();

  const handleAddNewGroup = () => {
    addNewGroup(rules, setRules, rule.id);
  };

  const currentRuleType = RULE_TYPE.find(
    data => data.value === rule?.event?.type,
  );

  const handleUpdateRule = useCallback(
    (key, value) => {
      updateRule({rules, setRules, ruleId: rule.id, key, value});
    },
    [rule.id, rules, setRules],
  );
  const handleUpdateConditionType = React.useCallback(
    type => {
      updateConditionType(rules, setRules, rule.id, type);
    },
    [rules, setRules, rule.id],
  );

  useEffect(() => {
    if (rule.conditionType === undefined) {
      handleUpdateConditionType('and');
    }
  }, [rule.id, rule.conditionType]);

  useEffect(() => {
    setIsRuleOpen(get?.currentActiveRule.isRuleOpen || rule.isRuleOpen);
  }, [rule.isRuleOpen]);

  return (
    <div className="mb-6 border-t border-[#EEF3FC] ">
      <ConfirmModal
        modalTitle="Confirm you want to delete this rule"
        modalBody="This action will delete all the configured data on this rule."
        buttonText="Delete rule"
        open={openDelete}
        setOpen={setOpenDelete}
        onClick={onDelete}
        isLoading={FLowData.deletingRule}
      />
      <div
        className="flex items-center p-4 border-b border-[#EEF3FC] cursor-pointer"
        onClick={() => setIsRuleOpen(!isRuleOpen)}
      >
        <div className="flex items-center">
          <button
            onClick={e => {
              e.stopPropagation();
              setOpenDelete(true);
            }}
            className="text-red-500 hover:text-red-700 mr-2"
            title="Delete rule"
          >
            <img src={trashDarkRedTwo} alt="Delete" />
          </button>
          <span className="font-medium">{rule?.name}</span>
        </div>
        <div className="ml-auto flex items-center">
          {isRuleOpen ? (
            <ChevronUp className="w-5 h-5 fill-black stroke-none" />
          ) : (
            <ChevronDown className="w-5 h-5 fill-black stroke-none" />
          )}
        </div>
      </div>

      {isRuleOpen && (
        <div className="p-4">
          <div className="mb-6">
            <div
              className="flex items-center justify-between mb-3 cursor-pointer"
              onClick={() => setIsActionOpen(!isActionOpen)}
            >
              <div className="flex items-center gap-3">
                <p className="text-[12px] text-[#73739B] font-medium  ">RULE</p>
                <div className="h-[1px] w-[270px] bg-[#DEDEE3]"></div>
                <div className="group">
                  <img
                    src={detectTooltip}
                    alt="tooltip"
                    className="cursor-pointer"
                  />
                  <div className="relative">
                    <img
                      src={toolTipTopDark}
                      className="absolute hidden group-hover:block "
                      alt="tooltip-top-dark"
                    />
                    <div
                      className="hidden group-hover:block w-[263px] p-4 fixed text-center h-[63px] bg-[#0B1C56] text-white text-[11px] rounded z-[9999]"
                      style={{top: '168px', right: 'calc(50% - 173px)'}}
                    >
                      Set customizable rules and conditions to spot fraud
                      through your user&apos;s journey{' '}
                    </div>
                  </div>
                </div>
              </div>
              {isActionOpen ? (
                <ChevronUp className="w-5 h-5 stroke-none fill-black" />
              ) : (
                <ChevronDown className="w-5 h-5 stroke-none fill-black" />
              )}
            </div>

            {isActionOpen && (
              <div className="bg-[#F9F9F9] rounded-md p-4">
                <div className="mb-4">
                  <label className="mb-1 block">Rule Name</label>
                  <input
                    type="text"
                    className="text-sm"
                    placeholder="Input your action name"
                    value={rule.name || ''}
                    onChange={e => {
                      handleUpdateRule('name', e.target.value);
                    }}
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-1 block">Rule Description</label>
                  <textarea
                    className="text-sm h-[70px]"
                    placeholder="Briefly describe the rule"
                    value={rule.description || ''}
                    onChange={e =>
                      handleUpdateRule('description', e.target.value)
                    }
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-1 block">Rule Score</label>
                  <input
                    type="text"
                    className="text-sm"
                    placeholder="Input a value between 30-100"
                    value={rule.risk_score || ''}
                    onChange={e => {
                      const inputValue = e.target.value;
                      if (
                        inputValue === '' ||
                        (inputValue >= 0 && inputValue <= 100)
                      ) {
                        handleUpdateRule('risk_score', +e.target.value);
                      }
                    }}
                  />
                </div>

                <div className="mb-6">
                  <label className="block mb-1">Rule Type</label>
                  <Select
                    defaultValue={currentRuleType}
                    onChange={data => {
                      handleUpdateRule('type', data.value);
                    }}
                    options={RULE_TYPE}
                    placeholder="High Risk"
                    className="basic-multi-select white-select flex-1"
                    classNamePrefix="react-select"
                    components={{DropdownIndicator}}
                    styles={customStyles}
                    menuPlacement="auto"
                  />
                </div>

                {isAggregateFunction && (
                  <TimePeriod
                    rulePeriod={rule.event?.params?.period || 'last_30m'}
                    onChange={periodData => {
                      const data = {
                        period: `last_${periodData?.count}${periodData.period}`,
                        period_minutes: convertToMinutes(
                          Number(periodData?.count),
                          periodData.period,
                        ),
                      };
                      handleUpdateRule('period', data);
                    }}
                  />
                )}
                {rule.groups.length > 1 && (
                  <div className="my-4 ">
                    <label className="mb-2 block font-medium">
                      Group Condition Type
                    </label>
                    <div className="flex space-x-4">
                      <label
                        className={`flex flex-row items-center px-4 py-2 rounded-md border cursor-pointer ${
                          rule.conditionType === 'and'
                            ? 'bg-brandBlue text-white border-none'
                            : 'bg-white text-[#033971] border-[#DFE1E7]'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`conditionType-${rule.id}`}
                          value="and"
                          checked={rule.conditionType === 'and'}
                          onChange={() => handleUpdateConditionType('and')}
                          className="mr-2 w-4 h-4 !accent-black"
                        />
                        <span>And</span>
                      </label>

                      <label
                        className={`flex flex-row items-center px-4 py-2 rounded-md border cursor-pointer ${
                          rule.conditionType === 'or'
                            ? 'bg-brandBlue text-white border-none'
                            : 'bg-white text-[#033971] border-[#DFE1E7]'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`conditionType-${rule.id}`}
                          value="or"
                          checked={rule.conditionType === 'or'}
                          onChange={() => handleUpdateConditionType('or')}
                          className="mr-2 w-4 h-4 !accent-black"
                        />
                        <span>Or</span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="h-[1px] bg-[#DEDEE3] w-full mt-4 mb-3" />

                <button
                  onClick={handleAddNewGroup}
                  className="w-full flex items-center justify-center text-sm border rounded-md py-2 text-[#033971] border-[#DFE1E7] hover:bg-grey-50"
                >
                  <img src={plusBlue} alt="Add" className="mr-2" />
                  Add New Group
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 ">
            {rule.groups.map((group, index) => (
              <Group
                key={group.id}
                ruleId={rule.id}
                group={group}
                rules={rules}
                setRules={setRules}
                index={index}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Rule;
